/**
 * Кольцо готовности.
 *
 * Показывает объективный счёт «выполнено из всего»: доля дуги — это то же
 * отношение, что и подпись рядом, а не отдельная оценка. Произвольный процент
 * готовности в системе не используется (документ «Итоговое описание», §16.2),
 * поэтому число внутри кольца — именно счёт, а не проценты.
 */

import { cn } from '@/lib/utils';

export function ProgressRing({
  value,
  max,
  size = 44,
  thickness = 4,
  showValue = true,
  className,
}: {
  value: number;
  max: number;
  size?: number;
  thickness?: number;
  showValue?: boolean;
  className?: string;
}) {
  const safeMax = Math.max(max, 0);
  const ratio = safeMax === 0 ? 0 : Math.min(value / safeMax, 1);
  const complete = safeMax > 0 && value >= safeMax;

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const label = `Выполнено ${value} из ${safeMax}`;

  return (
    <div
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-label={label}
      title={label}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border)"
          strokeWidth={thickness}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={complete ? 'var(--tone-success)' : 'var(--primary)'}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
          className="transition-[stroke-dashoffset] duration-500 ease-out"
        />
      </svg>

      {showValue ? (
        <span
          className="absolute inset-0 flex items-center justify-center font-semibold tabular-nums"
          style={{ fontSize: Math.max(9, size * 0.28) }}
          aria-hidden
        >
          {safeMax === 0 ? '—' : value}
        </span>
      ) : null}
    </div>
  );
}
